import type { AgentChartInput } from "./validate-chart-spec";
import type { AgentChartRow } from "./build-agent-chart-data";

const TIME_COLUMN_NAMES = new Set([
  "timestampms",
  "ts",
  "timestamp",
  "time",
  "datetime",
]);

const OHLCV_SKIP_FOR_AUTO_SERIES = new Set(["open", "high", "low", "volume"]);

export function sanitizeChartColumns(columns: unknown[]): string[] {
  return columns.filter(
    (c): c is string => typeof c === "string" && c.trim() !== "",
  );
}

export function isTimeColumnName(column: string | null | undefined): boolean {
  if (typeof column !== "string") return false;
  const t = column.trim();
  if (!t) return false;
  return TIME_COLUMN_NAMES.has(t.toLowerCase());
}

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseEpochMs(raw: unknown): number | null {
  if (raw == null) return null;

  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return null;
    if (ISO_DAY_RE.test(s)) {
      const t = Date.parse(`${s}T12:00:00`);
      return Number.isFinite(t) ? t : null;
    }
    const parsed = Date.parse(s);
    if (Number.isFinite(parsed)) return parsed;
  }

  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  // Seconds vs milliseconds
  if (n > 0 && n < 1_000_000_000_000) return n * 1000;
  return n;
}

export function inferChartXKey(
  columns: unknown[],
  labelKey?: string,
): string {
  const cols = sanitizeChartColumns(columns);
  if (cols.includes("timestampMs")) return "timestampMs";
  if (cols.includes("ts")) return "ts";
  if (cols.includes("timestamp")) return "timestamp";
  if (cols.includes("day")) return "day";
  if (labelKey && cols.includes(labelKey)) return labelKey;
  return cols[0] ?? "index";
}

export function inferChartXAxis(
  xKey: string | undefined,
  explicit?: AgentChartInput["xAxis"],
): AgentChartInput["xAxis"] {
  if (explicit) return explicit;
  if (!xKey) return "category";
  if (isTimeColumnName(xKey) || xKey === "day") return "time";
  return "category";
}

/** True when rows use numeric epoch ms on the x field (required for number/time X axis). */
export function chartRowsUseNumericX(
  rows: AgentChartRow[],
): boolean {
  if (rows.length === 0) return false;
  return rows.every((r) => typeof r.x === "number" && Number.isFinite(r.x));
}

/** Prefer close + indicators over full OHLCV when auto-picking line series. */
export function inferOhlcLineSeriesKeys(
  columns: unknown[],
  exclude: Set<string>,
): string[] {
  const candidates = sanitizeChartColumns(columns).filter(
    (c) => !exclude.has(c) && !isTimeColumnName(c),
  );

  if (candidates.includes("close")) {
    const focused = candidates.filter(
      (c) => !OHLCV_SKIP_FOR_AUTO_SERIES.has(c) || c === "close",
    );
    if (focused.length > 0) return focused;
  }

  return candidates;
}

export function formatChartTimeTick(ms: number): string {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return String(ms);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatChartTimeTooltip(ms: number): string {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return String(ms);
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Y domain that hugs series data (no forced zero baseline). */
export function computeChartYDomain(
  rows: AgentChartRow[],
  valueKeys: string[],
): [number, number] {
  let min = Infinity;
  let max = -Infinity;

  for (const row of rows) {
    for (const key of valueKeys) {
      const raw = key === "value" ? row.value : row[key];
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
      min = Math.min(min, raw);
      max = Math.max(max, raw);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1];
  }

  if (min === max) {
    const pad = Math.abs(min) * 0.01 || 0.01;
    return [min - pad, max + pad];
  }

  const span = max - min;
  const pad = span * 0.06;
  return [min - pad, max + pad];
}

export function normalizeAgentChartSpec(
  columns: unknown[],
  spec: AgentChartInput,
): AgentChartInput {
  const cols = sanitizeChartColumns(columns);
  const xKey =
    spec.xKey && cols.includes(spec.xKey)
      ? spec.xKey
      : inferChartXKey(cols, spec.labelKey);
  const xAxis = inferChartXAxis(xKey, spec.xAxis);

  return { ...spec, xKey, xAxis };
}
